import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/feature/PageHeader";
import usePageMeta from "@/hooks/usePageMeta";
import { characters, siteName } from "@/mocks/mangaData";

const relationshipRows = [
  { a: "苏念", rel: "纠缠 · 相互拉扯", b: "陆沉舟" },
  { a: "林晚晴", rel: "情敌 · 步步为营", b: "苏念" },
  { a: "顾言深", rel: "守护 · 温柔退路", b: "苏念" },
  { a: "沈清欢", rel: "身世引路人 · 亦敌亦友", b: "苏念" },
];

export default function Characters() {
  usePageMeta({
    title: "甜蜜惩罚漫画角色解析 | 人物关系与命运盘点 - 甜蜜惩罚漫画剧情追踪站",
    description:
      "甜蜜惩罚漫画角色解析：盘点男女主及关键配角的人物设定、成长弧光、关系网络与结局线索，看懂甜蜜惩罚漫画人物命运与角色关系。",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "甜蜜惩罚漫画角色解析",
      inLanguage: "zh-CN",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "角色解析" },
        ],
      },
    },
  });

  return (
    <Layout>
      <PageHeader
        title="角色解析 · 人物命运"
        subtitle="人物是理解剧情的钥匙。这里为每位关键角色建立档案，整理设定、成长弧光、关系网络与结局线索，让你在追更时对人设走向心中有数。"
        crumbs={[
          { label: "首页", path: "/" },
          { label: "角色解析" },
        ]}
      />

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold mb-7">角色档案</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {characters.map((c) => (
              <article
                key={c.id}
                className="group grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-5 rounded-lg border border-background-200 bg-background-50 p-5 hover:border-primary-300 transition-colors"
              >
                <div className="relative aspect-[4/5] overflow-hidden rounded-md">
                  <img
                    src={c.image}
                    alt={`甜蜜惩罚漫画角色 ${c.name}（${c.role}）立绘`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-2 top-2 rounded-full bg-primary-500 text-background-50 px-2.5 py-0.5 text-xs font-medium">
                    {c.role}
                  </span>
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2.5">
                    <h3 className="font-heading text-xl font-bold text-foreground-950">{c.name}</h3>
                    <span className="rounded-full bg-secondary-100 text-secondary-900 px-3 py-0.5 text-xs font-medium">
                      {c.tag}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-700">{c.alias}</p>
                  <p className="mt-3 text-sm leading-6 text-foreground-700">{c.desc}</p>

                  <div className="mt-4 flex-1 rounded-md bg-background-100 p-3.5">
                    <p className="text-xs font-semibold text-foreground-500 mb-1">成长弧光</p>
                    <p className="text-xs leading-5 text-foreground-700">{c.arc}</p>
                  </div>
                  <div className="mt-2.5 rounded-md bg-accent-50 border border-accent-100 p-3.5">
                    <p className="text-xs font-semibold text-accent-800 mb-1">结局线索</p>
                    <p className="text-xs leading-5 text-accent-900">{c.fate}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* 关系速览 */}
          <div className="mt-14 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold mb-6">关系速览</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {relationshipRows.map((row) => (
                <div
                  key={row.a + row.b}
                  className="flex items-center justify-between gap-3 rounded-md bg-background-50 border border-background-200 px-4 py-3.5"
                >
                  <span className="font-medium text-foreground-950 whitespace-nowrap">{row.a}</span>
                  <span className="flex flex-1 items-center gap-2 text-xs text-foreground-500 justify-center">
                    <i className="ri-links-line text-primary-500 text-base leading-none"></i>
                    <span className="whitespace-nowrap">{row.rel}</span>
                  </span>
                  <span className="font-medium text-foreground-950 whitespace-nowrap">{row.b}</span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs leading-6 text-foreground-600">
              以上关系基于已公开分话整理，会随着剧情推进持续更新。角色命运与关系走向请以官方更新内容为准。
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}